import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { rejectOrder } from '../../api/orders';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';
import { Alert } from '../../components/ui/Alert';
import type { Order } from '../../types/api';

export function RejectOrderModal({
  order,
  open,
  onClose,
  onRejected,
}: {
  order: Order;
  open: boolean;
  onClose: () => void;
  onRejected?: () => void;
}) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => rejectOrder(order.id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['order', order.id] });
      queryClient.invalidateQueries({ queryKey: ['orders-queue'] });
      showToast(`Order "${order.reference}" rejected.`);
      setReason('');
      onClose();
      onRejected?.();
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not reject the order.')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!reason.trim()) {
      setError('Please give a reason - the requester will see it.');
      return;
    }
    mutation.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Reject "${order.reference}"`}
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button form="reject-order-form" type="submit" variant="danger" isLoading={mutation.isPending}>
            Reject order
          </Button>
        </>
      }
    >
      <form id="reject-order-form" onSubmit={handleSubmit} className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        <Input
          label="Reason"
          required
          autoFocus
          hint="Sent to the requester so they can amend and resubmit."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </form>
    </Modal>
  );
}
